export interface ThemeColors {
  background: string;
  surface: string;
  surfaceRaised: string;
  overlay: string;
  border: string;
  borderSubtle: string;
  foreground: string;
  foregroundMuted: string;
  foregroundFaint: string;
  foregroundBright: string;
  primary: string;
  primaryHover: string;
  primaryMuted: string;
  accent: string;
  accentHover: string;
  accentMuted: string;
  success: string;
  warning: string;
  error: string;
  info: string;
}

export interface Theme {
  id: number;
  name: string;
  description: string;
  colors: ThemeColors;
}

export const THEMES: Theme[] = [
  {
    id: 0,
    name: 'Ayu × Horizon',
    description: 'Deep blacks, warm coral & blue — the default',
    colors: {
      background: '#0a0e14',
      surface: '#10141a',
      surfaceRaised: '#181d24',
      overlay: '#1f252d',
      border: '#1c2229',
      borderSubtle: '#141a20',
      foreground: '#d8d2ca',
      foregroundMuted: '#a69590',
      foregroundFaint: '#6e5f5a',
      foregroundBright: '#ede8e2',
      primary: '#f09483',
      primaryHover: '#f4a99e',
      primaryMuted: '#5c3a34',
      accent: '#59c2ff',
      accentHover: '#7dd0ff',
      accentMuted: '#1e4a66',
      success: '#29d398',
      warning: '#e6b450',
      error: '#e95678',
      info: '#59c2ff',
    },
  },
  {
    id: 21,
    name: 'Ayu × Horizon Violet',
    description: 'Deep blacks, violet primary with warm text',
    colors: {
      background: '#0a0e14',
      surface: '#10141a',
      surfaceRaised: '#181d24',
      overlay: '#1f252d',
      border: '#1c2229',
      borderSubtle: '#141a20',
      foreground: '#d4cfe0',
      foregroundMuted: '#9a8ea8',
      foregroundFaint: '#5e536a',
      foregroundBright: '#eae5f2',
      primary: '#b877db',
      primaryHover: '#cc99e8',
      primaryMuted: '#4e2e6e',
      accent: '#f09483',
      accentHover: '#f4a99e',
      accentMuted: '#5c3a34',
      success: '#29d398',
      warning: '#e6b450',
      error: '#e95678',
      info: '#59c2ff',
    },
  },
  {
    id: 22,
    name: 'Ayu × Horizon Gold',
    description: 'Deep blacks, golden amber with warm text',
    colors: {
      background: '#0a0e14',
      surface: '#10141a',
      surfaceRaised: '#181d24',
      overlay: '#1f252d',
      border: '#1c2229',
      borderSubtle: '#141a20',
      foreground: '#dbd5c6',
      foregroundMuted: '#ab9e86',
      foregroundFaint: '#6d6250',
      foregroundBright: '#f0ebe0',
      primary: '#e6b450',
      primaryHover: '#f0c76e',
      primaryMuted: '#5c4a20',
      accent: '#59c2ff',
      accentHover: '#7dd0ff',
      accentMuted: '#1e4a66',
      success: '#29d398',
      warning: '#f09483',
      error: '#e95678',
      info: '#59c2ff',
    },
  },
  {
    id: 24,
    name: 'Ayu × Horizon Gold Bright',
    description: 'Deep blacks, brighter gold with luminous warm text',
    colors: {
      background: '#0b1018',
      surface: '#121820',
      surfaceRaised: '#1c2330',
      overlay: '#243040',
      border: '#222a36',
      borderSubtle: '#18202a',
      foreground: '#e8e0d0',
      foregroundMuted: '#c4b498',
      foregroundFaint: '#887a62',
      foregroundBright: '#f5f0e5',
      primary: '#f0c050',
      primaryHover: '#f5d078',
      primaryMuted: '#6e5820',
      accent: '#6dcfff',
      accentHover: '#90ddff',
      accentMuted: '#265a7a',
      success: '#3ee0a5',
      warning: '#f4a070',
      error: '#f06080',
      info: '#6dcfff',
    },
  },
  {
    id: 23,
    name: 'Ayu × Horizon Teal',
    description: 'Deep blacks, teal primary with coral accents',
    colors: {
      background: '#0a0e14',
      surface: '#10141a',
      surfaceRaised: '#181d24',
      overlay: '#1f252d',
      border: '#1c2229',
      borderSubtle: '#141a20',
      foreground: '#ccd8d4',
      foregroundMuted: '#88a09a',
      foregroundFaint: '#506862',
      foregroundBright: '#e4f0ec',
      primary: '#29d398',
      primaryHover: '#4de0af',
      primaryMuted: '#145a40',
      accent: '#f09483',
      accentHover: '#f4a99e',
      accentMuted: '#5c3a34',
      success: '#59c2ff',
      warning: '#e6b450',
      error: '#e95678',
      info: '#59c2ff',
    },
  },
  {
    id: 25,
    name: 'Batman',
    description: 'Gotham black with utility belt yellow',
    colors: {
      background: '#0a0a0c',
      surface: '#121215',
      surfaceRaised: '#1c1c20',
      overlay: '#26262c',
      border: '#1f1f24',
      borderSubtle: '#16161a',
      foreground: '#d4d4d8',
      foregroundMuted: '#8a8a92',
      foregroundFaint: '#5a5a62',
      foregroundBright: '#f4f4f6',
      primary: '#ffd23f',
      primaryHover: '#ffdf6e',
      primaryMuted: '#7f6920',
      accent: '#5a8db8',
      accentHover: '#7ba3c8',
      accentMuted: '#2d465c',
      success: '#4ade80',
      warning: '#ffd23f',
      error: '#dc2626',
      info: '#5a8db8',
    },
  },
  {
    id: 26,
    name: 'Wonder Woman',
    description: 'Amazonian red with Themyscira gold',
    colors: {
      background: '#0e0a14',
      surface: '#161020',
      surfaceRaised: '#21182d',
      overlay: '#2c2138',
      border: '#1d1528',
      borderSubtle: '#150f1d',
      foreground: '#e8d8d6',
      foregroundMuted: '#a89090',
      foregroundFaint: '#6e5a5e',
      foregroundBright: '#f8eae8',
      primary: '#d5252b',
      primaryHover: '#e64449',
      primaryMuted: '#6a1316',
      accent: '#ffd84d',
      accentHover: '#ffe478',
      accentMuted: '#7f6c26',
      success: '#3ad97a',
      warning: '#ffd84d',
      error: '#ff4d6d',
      info: '#5b8def',
    },
  },
  {
    id: 27,
    name: 'Captain America',
    description: 'Star-spangled red, white, and blue',
    colors: {
      background: '#0a1428',
      surface: '#0f1d36',
      surfaceRaised: '#172945',
      overlay: '#203659',
      border: '#13243f',
      borderSubtle: '#0d1a30',
      foreground: '#e0e8f0',
      foregroundMuted: '#9bb0c8',
      foregroundFaint: '#5d7596',
      foregroundBright: '#f5f8fc',
      primary: '#c8102e',
      primaryHover: '#dc2945',
      primaryMuted: '#641019',
      accent: '#3a6ad4',
      accentHover: '#5a86e0',
      accentMuted: '#1d3567',
      success: '#3ee082',
      warning: '#f0c050',
      error: '#e0314a',
      info: '#5a86e0',
    },
  },
  {
    id: 28,
    name: 'Hulk',
    description: 'Gamma green with torn purple pants',
    colors: {
      background: '#0a120c',
      surface: '#101a13',
      surfaceRaised: '#18261c',
      overlay: '#213527',
      border: '#1a2a1f',
      borderSubtle: '#121e16',
      foreground: '#d4e5d8',
      foregroundMuted: '#8aa896',
      foregroundFaint: '#566b5e',
      foregroundBright: '#eaf5ee',
      primary: '#6ec531',
      primaryHover: '#8ad84f',
      primaryMuted: '#3a6618',
      accent: '#9b59ff',
      accentHover: '#b27bff',
      accentMuted: '#4d2c7f',
      success: '#3ee082',
      warning: '#e6b450',
      error: '#e95678',
      info: '#59c2ff',
    },
  },
  {
    id: 29,
    name: 'Venom',
    description: 'Symbiote black with blood-red tongue',
    colors: {
      background: '#050506',
      surface: '#0c0c0e',
      surfaceRaised: '#16161a',
      overlay: '#202024',
      border: '#1a1a1e',
      borderSubtle: '#101012',
      foreground: '#d8d8dc',
      foregroundMuted: '#7a7a82',
      foregroundFaint: '#4a4a52',
      foregroundBright: '#ffffff',
      primary: '#e8e8ec',
      primaryHover: '#ffffff',
      primaryMuted: '#5a5a60',
      accent: '#c8102e',
      accentHover: '#e02944',
      accentMuted: '#5e0a17',
      success: '#5ea672',
      warning: '#c4a44a',
      error: '#dc1422',
      info: '#7a7a82',
    },
  },
  {
    id: 30,
    name: 'Spider-Man',
    description: 'Web-slinger red with electric blue',
    colors: {
      background: '#0a1020',
      surface: '#0f1729',
      surfaceRaised: '#172238',
      overlay: '#202d48',
      border: '#152038',
      borderSubtle: '#0d1426',
      foreground: '#dce4f2',
      foregroundMuted: '#8d9bb8',
      foregroundFaint: '#566380',
      foregroundBright: '#f2f6ff',
      primary: '#e1242f',
      primaryHover: '#f04450',
      primaryMuted: '#6e1218',
      accent: '#1e6cd6',
      accentHover: '#3d8aef',
      accentMuted: '#0f3668',
      success: '#3ee082',
      warning: '#f0c050',
      error: '#ff4d6d',
      info: '#3d8aef',
    },
  },
  {
    id: 31,
    name: 'Harry Potter',
    description: 'Gryffindor scarlet with house gold',
    colors: {
      background: '#0e0a08',
      surface: '#16100c',
      surfaceRaised: '#221913',
      overlay: '#2e231a',
      border: '#1f1611',
      borderSubtle: '#15100b',
      foreground: '#e8d9b8',
      foregroundMuted: '#a89272',
      foregroundFaint: '#6c5a44',
      foregroundBright: '#f5e8c8',
      primary: '#ae0001',
      primaryHover: '#cc1c1d',
      primaryMuted: '#560001',
      accent: '#d3a625',
      accentHover: '#e6bd4a',
      accentMuted: '#6a5212',
      success: '#5ea672',
      warning: '#d3a625',
      error: '#cc1c1d',
      info: '#946b2d',
    },
  },
  {
    id: 1,
    name: 'Claude Console',
    description: 'Warm oranges with teal accents',
    colors: {
      background: '#1a1a1a',
      surface: '#242424',
      surfaceRaised: '#2d2d2d',
      overlay: '#363636',
      border: '#3a3a3a',
      borderSubtle: '#2f2f2f',
      foreground: '#e0e0e0',
      foregroundMuted: '#a0a0a0',
      foregroundFaint: '#6a6a6a',
      foregroundBright: '#f0f0f0',
      primary: '#f97316',
      primaryHover: '#fb923c',
      primaryMuted: '#7c3a0d',
      accent: '#14b8a6',
      accentHover: '#2dd4bf',
      accentMuted: '#0a5c53',
      success: '#10b981',
      warning: '#f59e0b',
      error: '#ef4444',
      info: '#3b82f6',
    },
  },
  {
    id: 2,
    name: 'IntelliJ Darcula',
    description: 'Classic JetBrains dark theme',
    colors: {
      background: '#2b2b2b',
      surface: '#3c3f41',
      surfaceRaised: '#4b4f52',
      overlay: '#515658',
      border: '#323232',
      borderSubtle: '#2a2a2a',
      foreground: '#bbbbbb',
      foregroundMuted: '#909090',
      foregroundFaint: '#606366',
      foregroundBright: '#d4d4d4',
      primary: '#4a9eff',
      primaryHover: '#6cb4ff',
      primaryMuted: '#25507f',
      accent: '#cc7832',
      accentHover: '#d99050',
      accentMuted: '#663c19',
      success: '#629755',
      warning: '#bbb529',
      error: '#bc3f3c',
      info: '#4a9eff',
    },
  },
  {
    id: 3,
    name: 'One Dark Pro',
    description: "VSCode's most popular theme",
    colors: {
      background: '#282c34',
      surface: '#21252b',
      surfaceRaised: '#2c313a',
      overlay: '#353b45',
      border: '#181a1f',
      borderSubtle: '#1e2127',
      foreground: '#abb2bf',
      foregroundMuted: '#5c6370',
      foregroundFaint: '#4b5263',
      foregroundBright: '#d7dae0',
      primary: '#61afef',
      primaryHover: '#84c0f4',
      primaryMuted: '#305877',
      accent: '#c678dd',
      accentHover: '#d5a0e8',
      accentMuted: '#633c6e',
      success: '#98c379',
      warning: '#e5c07b',
      error: '#e06c75',
      info: '#61afef',
    },
  },
  {
    id: 4,
    name: 'Dracula',
    description: 'Vibrant purples and pinks',
    colors: {
      background: '#282a36',
      surface: '#21222c',
      surfaceRaised: '#343746',
      overlay: '#3e4155',
      border: '#191a21',
      borderSubtle: '#21222c',
      foreground: '#f8f8f2',
      foregroundMuted: '#6272a4',
      foregroundFaint: '#44475a',
      foregroundBright: '#ffffff',
      primary: '#bd93f9',
      primaryHover: '#d4b3ff',
      primaryMuted: '#5e497c',
      accent: '#ff79c6',
      accentHover: '#ff9cd8',
      accentMuted: '#7f3c63',
      success: '#50fa7b',
      warning: '#f1fa8c',
      error: '#ff5555',
      info: '#8be9fd',
    },
  },
  {
    id: 5,
    name: 'Nord',
    description: 'Arctic, north-bluish color palette',
    colors: {
      background: '#2e3440',
      surface: '#3b4252',
      surfaceRaised: '#434c5e',
      overlay: '#4c566a',
      border: '#2e3440',
      borderSubtle: '#363d4a',
      foreground: '#eceff4',
      foregroundMuted: '#d8dee9',
      foregroundFaint: '#4c566a',
      foregroundBright: '#ffffff',
      primary: '#88c0d0',
      primaryHover: '#8fbcbb',
      primaryMuted: '#446068',
      accent: '#81a1c1',
      accentHover: '#96b4d0',
      accentMuted: '#405060',
      success: '#a3be8c',
      warning: '#ebcb8b',
      error: '#bf616a',
      info: '#5e81ac',
    },
  },
  {
    id: 6,
    name: 'Tokyo Night',
    description: 'Modern dark theme with vibrant colors',
    colors: {
      background: '#1a1b26',
      surface: '#24283b',
      surfaceRaised: '#414868',
      overlay: '#4e5579',
      border: '#1f2335',
      borderSubtle: '#24283b',
      foreground: '#c0caf5',
      foregroundMuted: '#a9b1d6',
      foregroundFaint: '#565f89',
      foregroundBright: '#e0e4ff',
      primary: '#7aa2f7',
      primaryHover: '#7dcfff',
      primaryMuted: '#3d517b',
      accent: '#bb9af7',
      accentHover: '#cdb4ff',
      accentMuted: '#5d4d7b',
      success: '#9ece6a',
      warning: '#e0af68',
      error: '#f7768e',
      info: '#7aa2f7',
    },
  },
  {
    id: 7,
    name: 'Gruvbox Dark',
    description: 'Retro groove with warm colors',
    colors: {
      background: '#282828',
      surface: '#3c3836',
      surfaceRaised: '#504945',
      overlay: '#5a524c',
      border: '#1d2021',
      borderSubtle: '#32302f',
      foreground: '#ebdbb2',
      foregroundMuted: '#a89984',
      foregroundFaint: '#665c54',
      foregroundBright: '#fbf1c7',
      primary: '#fe8019',
      primaryHover: '#fabd2f',
      primaryMuted: '#7f400c',
      accent: '#d3869b',
      accentHover: '#e0a0b0',
      accentMuted: '#69434d',
      success: '#b8bb26',
      warning: '#fabd2f',
      error: '#fb4934',
      info: '#83a598',
    },
  },
  {
    id: 8,
    name: 'Monokai Pro',
    description: 'Professional developer favorite',
    colors: {
      background: '#2d2a2e',
      surface: '#221f22',
      surfaceRaised: '#403e41',
      overlay: '#4a484b',
      border: '#19181a',
      borderSubtle: '#2d2a2e',
      foreground: '#fcfcfa',
      foregroundMuted: '#939293',
      foregroundFaint: '#5b595c',
      foregroundBright: '#ffffff',
      primary: '#ffd866',
      primaryHover: '#ffe98f',
      primaryMuted: '#7f6c33',
      accent: '#fc9867',
      accentHover: '#fdad83',
      accentMuted: '#7e4c33',
      success: '#a9dc76',
      warning: '#ffd866',
      error: '#ff6188',
      info: '#78dce8',
    },
  },
  {
    id: 9,
    name: 'Material Darker',
    description: 'Google Material Design dark',
    colors: {
      background: '#212121',
      surface: '#292929',
      surfaceRaised: '#303030',
      overlay: '#383838',
      border: '#1a1a1a',
      borderSubtle: '#252525',
      foreground: '#eeffff',
      foregroundMuted: '#b0bec5',
      foregroundFaint: '#616161',
      foregroundBright: '#ffffff',
      primary: '#82aaff',
      primaryHover: '#a4bdff',
      primaryMuted: '#41557f',
      accent: '#c792ea',
      accentHover: '#d5acf0',
      accentMuted: '#634975',
      success: '#c3e88d',
      warning: '#ffcb6b',
      error: '#f07178',
      info: '#82aaff',
    },
  },
  {
    id: 10,
    name: 'Ayu Dark',
    description: 'Simple, bright and elegant',
    colors: {
      background: '#0a0e14',
      surface: '#0d1016',
      surfaceRaised: '#151a1e',
      overlay: '#1c2228',
      border: '#01060e',
      borderSubtle: '#0d1016',
      foreground: '#b3b1ad',
      foregroundMuted: '#626a73',
      foregroundFaint: '#3e4b59',
      foregroundBright: '#d9d7d4',
      primary: '#59c2ff',
      primaryHover: '#73ccff',
      primaryMuted: '#2c617f',
      accent: '#ffb454',
      accentHover: '#ffc478',
      accentMuted: '#7f5a2a',
      success: '#c2d94c',
      warning: '#e6b450',
      error: '#f07178',
      info: '#59c2ff',
    },
  },
  {
    id: 11,
    name: 'Catppuccin Mocha',
    description: 'Soothing pastel theme',
    colors: {
      background: '#1e1e2e',
      surface: '#181825',
      surfaceRaised: '#313244',
      overlay: '#3b3c52',
      border: '#11111b',
      borderSubtle: '#1e1e2e',
      foreground: '#cdd6f4',
      foregroundMuted: '#bac2de',
      foregroundFaint: '#585b70',
      foregroundBright: '#e4e8f8',
      primary: '#89b4fa',
      primaryHover: '#a6c8ff',
      primaryMuted: '#445a7d',
      accent: '#f5c2e7',
      accentHover: '#f8d4ef',
      accentMuted: '#7a6173',
      success: '#a6e3a1',
      warning: '#f9e2af',
      error: '#f38ba8',
      info: '#89dceb',
    },
  },
  {
    id: 12,
    name: 'GitHub Dark Dimmed',
    description: "GitHub's refined dark theme",
    colors: {
      background: '#22272e',
      surface: '#2d333b',
      surfaceRaised: '#373e47',
      overlay: '#414950',
      border: '#444c56',
      borderSubtle: '#373e47',
      foreground: '#adbac7',
      foregroundMuted: '#768390',
      foregroundFaint: '#545d68',
      foregroundBright: '#cdd9e5',
      primary: '#539bf5',
      primaryHover: '#6cb6ff',
      primaryMuted: '#294d7a',
      accent: '#d2a8ff',
      accentHover: '#debfff',
      accentMuted: '#69547f',
      success: '#57ab5a',
      warning: '#c69026',
      error: '#e5534b',
      info: '#539bf5',
    },
  },
  {
    id: 13,
    name: 'Horizon',
    description: 'Warm dark theme with sunset colors',
    colors: {
      background: '#1c1e26',
      surface: '#232530',
      surfaceRaised: '#2e303e',
      overlay: '#383a4a',
      border: '#16161c',
      borderSubtle: '#232530',
      foreground: '#e3e6ee',
      foregroundMuted: '#b877db',
      foregroundFaint: '#6c6f93',
      foregroundBright: '#f0f2f8',
      primary: '#f09483',
      primaryHover: '#f4a99e',
      primaryMuted: '#784a41',
      accent: '#fab795',
      accentHover: '#fcc8ab',
      accentMuted: '#7d5b4a',
      success: '#29d398',
      warning: '#fac39a',
      error: '#e95678',
      info: '#25b0bc',
    },
  },
  {
    id: 14,
    name: 'Palenight',
    description: 'Elegant Material variant',
    colors: {
      background: '#292d3e',
      surface: '#32374d',
      surfaceRaised: '#3b4259',
      overlay: '#444b63',
      border: '#1b1e2b',
      borderSubtle: '#292d3e',
      foreground: '#a6accd',
      foregroundMuted: '#676e95',
      foregroundFaint: '#4e5579',
      foregroundBright: '#c8cde8',
      primary: '#82aaff',
      primaryHover: '#a1bdff',
      primaryMuted: '#41557f',
      accent: '#c792ea',
      accentHover: '#d5acf0',
      accentMuted: '#634975',
      success: '#c3e88d',
      warning: '#ffcb6b',
      error: '#ff5370',
      info: '#82aaff',
    },
  },
  {
    id: 15,
    name: 'Cobalt2',
    description: 'Deep blues with bright accents',
    colors: {
      background: '#122738',
      surface: '#193549',
      surfaceRaised: '#1f4662',
      overlay: '#255580',
      border: '#0d1d2a',
      borderSubtle: '#193549',
      foreground: '#ffffff',
      foregroundMuted: '#0088ff',
      foregroundFaint: '#1d3b53',
      foregroundBright: '#ffffff',
      primary: '#ffc600',
      primaryHover: '#ffd34e',
      primaryMuted: '#7f6300',
      accent: '#ff2c70',
      accentHover: '#ff5c90',
      accentMuted: '#7f1638',
      success: '#3ad900',
      warning: '#ffc600',
      error: '#ff0000',
      info: '#0088ff',
    },
  },
  {
    id: 16,
    name: "Synthwave '84",
    description: 'Neon cyberpunk vibes',
    colors: {
      background: '#262335',
      surface: '#2a2139',
      surfaceRaised: '#34294f',
      overlay: '#3e3360',
      border: '#1b1721',
      borderSubtle: '#262335',
      foreground: '#f7f7fb',
      foregroundMuted: '#b6b1b1',
      foregroundFaint: '#6d6d6d',
      foregroundBright: '#ffffff',
      primary: '#ff7edb',
      primaryHover: '#ffa7e8',
      primaryMuted: '#7f3f6d',
      accent: '#36f9f6',
      accentHover: '#6ffbf9',
      accentMuted: '#1b7c7b',
      success: '#72f1b8',
      warning: '#fede5d',
      error: '#fe4450',
      info: '#36f9f6',
    },
  },
  {
    id: 17,
    name: 'Night Owl',
    description: 'Fine-tuned for night coding',
    colors: {
      background: '#011627',
      surface: '#01111d',
      surfaceRaised: '#1d3b53',
      overlay: '#2a4d68',
      border: '#0b2942',
      borderSubtle: '#011627',
      foreground: '#d6deeb',
      foregroundMuted: '#5f7e97',
      foregroundFaint: '#2d5164',
      foregroundBright: '#eef4fc',
      primary: '#82aaff',
      primaryHover: '#a3bfff',
      primaryMuted: '#41557f',
      accent: '#c792ea',
      accentHover: '#d5acf0',
      accentMuted: '#634975',
      success: '#addb67',
      warning: '#ecc48d',
      error: '#ef5350',
      info: '#82aaff',
    },
  },
  {
    id: 18,
    name: 'Andromeda',
    description: 'Space-themed with purples',
    colors: {
      background: '#23262e',
      surface: '#1b1d23',
      surfaceRaised: '#2b2e38',
      overlay: '#353845',
      border: '#181a1f',
      borderSubtle: '#23262e',
      foreground: '#d5ced9',
      foregroundMuted: '#a0a0a0',
      foregroundFaint: '#606060',
      foregroundBright: '#ede7f2',
      primary: '#f92aad',
      primaryHover: '#ff5ec7',
      primaryMuted: '#7c1556',
      accent: '#ee5d43',
      accentHover: '#f28070',
      accentMuted: '#772e21',
      success: '#96e072',
      warning: '#f39c12',
      error: '#ee5d43',
      info: '#6ec8f4',
    },
  },
  {
    id: 19,
    name: 'Oceanic Next',
    description: 'Ocean-inspired blues and greens',
    colors: {
      background: '#1b2b34',
      surface: '#343d46',
      surfaceRaised: '#4f5b66',
      overlay: '#5c6873',
      border: '#0f1c23',
      borderSubtle: '#1b2b34',
      foreground: '#c0c5ce',
      foregroundMuted: '#65737e',
      foregroundFaint: '#4f5b66',
      foregroundBright: '#d8dee9',
      primary: '#6699cc',
      primaryHover: '#89b3e0',
      primaryMuted: '#334c66',
      accent: '#c594c5',
      accentHover: '#d3aed3',
      accentMuted: '#624a62',
      success: '#99c794',
      warning: '#fac863',
      error: '#ec5f67',
      info: '#6699cc',
    },
  },
  {
    id: 20,
    name: 'JetBrains New Dark',
    description: 'Modern JetBrains 2024 theme',
    colors: {
      background: '#1e1f22',
      surface: '#2b2d30',
      surfaceRaised: '#3c3f41',
      overlay: '#45484b',
      border: '#2d2f32',
      borderSubtle: '#25272a',
      foreground: '#bcbec4',
      foregroundMuted: '#868a91',
      foregroundFaint: '#5e6167',
      foregroundBright: '#dfe1e5',
      primary: '#4f9eff',
      primaryHover: '#6fadff',
      primaryMuted: '#274f7f',
      accent: '#a97bff',
      accentHover: '#bfa0ff',
      accentMuted: '#543d7f',
      success: '#59a869',
      warning: '#dca561',
      error: '#c77074',
      info: '#4f9eff',
    },
  },
];

/** CSS custom property name mapping */
const CSS_VAR_MAP: Record<keyof ThemeColors, string> = {
  background: '--color-background',
  surface: '--color-surface',
  surfaceRaised: '--color-surface-raised',
  overlay: '--color-overlay',
  border: '--color-border',
  borderSubtle: '--color-border-subtle',
  foreground: '--color-foreground',
  foregroundMuted: '--color-foreground-muted',
  foregroundFaint: '--color-foreground-faint',
  foregroundBright: '--color-foreground-bright',
  primary: '--color-primary',
  primaryHover: '--color-primary-hover',
  primaryMuted: '--color-primary-muted',
  accent: '--color-accent',
  accentHover: '--color-accent-hover',
  accentMuted: '--color-accent-muted',
  success: '--color-success',
  warning: '--color-warning',
  error: '--color-error',
  info: '--color-info',
};

const STORAGE_KEY = 'theme-id';

export function applyTheme(theme: Theme): void {
  const root = document.documentElement;
  for (const [key, cssVar] of Object.entries(CSS_VAR_MAP)) {
    root.style.setProperty(cssVar, theme.colors[key as keyof ThemeColors]);
  }
  localStorage.setItem(STORAGE_KEY, String(theme.id));
}

export function clearTheme(): void {
  const root = document.documentElement;
  for (const cssVar of Object.values(CSS_VAR_MAP)) {
    root.style.removeProperty(cssVar);
  }
  localStorage.removeItem(STORAGE_KEY);
}

export function loadSavedTheme(): Theme | null {
  const saved = localStorage.getItem(STORAGE_KEY);
  if (!saved) return null;
  const id = Number(saved);
  return THEMES.find((t) => t.id === id) ?? null;
}
